import React, { useEffect, useMemo, useState } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Badge } from "@/components/ui/badge";
import api from "@/lib/api";
import { toast } from "sonner";
import { MessagesSquare, SendHorizonal, Loader2, CheckCircle2, Trash2, RotateCcw, Reply, X } from "lucide-react";
import { cn } from "@/lib/utils";

const MENTION_RE = /(@[\w.+-]+@[\w-]+\.[\w.-]+)/g;

function MentionText({ text }) {
  const parts = String(text || "").split(MENTION_RE);
  return (
    <>
      {parts.map((p, i) => p.match(MENTION_RE)
        ? <span key={i} className="text-[color:var(--brand-ocean)] font-medium">{p}</span>
        : p)}
    </>
  );
}

function CommentCard({ c, isReply, onResolve, onDelete, onReply }) {
  return (
    <div data-testid="comment-item" className={cn("glass-card p-3", c.resolved && "opacity-60", isReply && "ml-7")}>
      <div className="flex items-center gap-2">
        <div className="w-6 h-6 rounded-full bg-[color:rgba(43,78,255,0.12)] text-[color:var(--brand-ocean)] flex items-center justify-center text-[10px] font-bold">{(c.user_name || "?").slice(0, 2).toUpperCase()}</div>
        <span className="text-xs font-semibold text-[color:var(--ink)] flex-1 truncate">{c.user_name}</span>
        <span className="text-[10px] text-[color:var(--ink-3)]">{new Date(c.created_at).toLocaleDateString()}</span>
      </div>
      <p className="text-sm text-[color:var(--ink-2)] mt-1.5 whitespace-pre-wrap break-words"><MentionText text={c.text} /></p>
      <div className="flex items-center gap-1 mt-1.5">
        {c.resolved && <Badge className="bg-[color:rgba(0,194,168,0.14)] text-[color:var(--success)] border-0 text-[10px]">Resolved</Badge>}
        <span className="flex-1" />
        {!isReply && (
          <button data-testid="comment-reply-btn" title="Reply" onClick={() => onReply(c)} className="text-[color:var(--ink-3)] hover:text-[color:var(--brand-ocean)]">
            <Reply className="w-3.5 h-3.5" />
          </button>
        )}
        <button data-testid="comment-resolve-btn" title={c.resolved ? "Reopen" : "Resolve"} onClick={() => onResolve(c)} className="text-[color:var(--ink-3)] hover:text-[color:var(--brand-mint)] ml-1.5">
          {c.resolved ? <RotateCcw className="w-3.5 h-3.5" /> : <CheckCircle2 className="w-3.5 h-3.5" />}
        </button>
        <button data-testid="comment-delete-btn" title="Delete" onClick={() => onDelete(c)} className="text-[color:var(--ink-3)] hover:text-[color:var(--danger)] ml-1.5"><Trash2 className="w-3.5 h-3.5" /></button>
      </div>
    </div>
  );
}

export function CommentsPanel({ open, onOpenChange, dashboardId }) {
  const [items, setItems] = useState([]);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [replyTo, setReplyTo] = useState(null);
  const [hideResolved, setHideResolved] = useState(false);

  const load = () => api.get(`/dashboards/${dashboardId}/comments`).then(({ data }) => setItems(data)).catch(() => {});
  useEffect(() => {
    if (!open) return;
    load();
    const t = setInterval(load, 20000); // live-ish collaboration refresh
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, dashboardId]);

  const threads = useMemo(() => {
    const shown = hideResolved ? items.filter((c) => !c.resolved) : items;
    const roots = shown.filter((c) => !c.parent_id);
    const byParent = {};
    shown.forEach((c) => { if (c.parent_id) (byParent[c.parent_id] = byParent[c.parent_id] || []).push(c); });
    Object.values(byParent).forEach((arr) => arr.sort((a, b) => a.created_at.localeCompare(b.created_at)));
    return roots.map((r) => ({ root: r, replies: byParent[r.id] || [] }));
  }, [items, hideResolved]);

  const post = async () => {
    if (!text.trim()) return;
    setBusy(true);
    try {
      await api.post(`/dashboards/${dashboardId}/comments`, { text: text.trim(), parent_id: replyTo?.id || null });
      setText(""); setReplyTo(null);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to post comment"); }
    finally { setBusy(false); }
  };
  const resolve = async (c) => {
    try { await api.patch(`/dashboards/${dashboardId}/comments/${c.id}`, { resolved: !c.resolved }); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const del = async (c) => {
    try { await api.delete(`/dashboards/${dashboardId}/comments/${c.id}`); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-sm p-0 flex flex-col bg-[color:var(--canvas)]" data-testid="comments-panel">
        <SheetHeader className="px-5 py-4 border-b border-[color:var(--glass-border)] bg-[color:var(--glass-bg)]">
          <SheetTitle className="flex items-center gap-2 font-display">
            <MessagesSquare className="w-5 h-5 text-[color:var(--brand-ocean)]" />Comments
            <Badge variant="outline" className="ml-1 text-[color:var(--ink-3)]">{items.length}</Badge>
            <button data-testid="comments-hide-resolved" onClick={() => setHideResolved(!hideResolved)}
              className={cn("ml-auto text-[10px] px-2 py-0.5 rounded-full border transition-colors",
                hideResolved ? "bg-[color:var(--brand-ocean)] text-white border-transparent" : "text-[color:var(--ink-3)] border-[color:var(--glass-border)]")}>
              {hideResolved ? "Showing open" : "Hide resolved"}
            </button>
          </SheetTitle>
        </SheetHeader>
        <ScrollArea className="flex-1 px-4 py-3">
          {threads.length === 0 && <p className="text-sm text-[color:var(--ink-3)] text-center py-8">No comments yet. Mention teammates with @their-email to notify them.</p>}
          <div className="space-y-2.5">
            {threads.map(({ root, replies }) => (
              <div key={root.id} className="space-y-1.5">
                <CommentCard c={root} onResolve={resolve} onDelete={del} onReply={setReplyTo} />
                {replies.map((r) => <CommentCard key={r.id} c={r} isReply onResolve={resolve} onDelete={del} onReply={setReplyTo} />)}
              </div>
            ))}
          </div>
        </ScrollArea>
        <div className="p-3 border-t border-[color:var(--glass-border)] bg-[color:var(--glass-bg)]">
          {replyTo && (
            <div data-testid="comment-replying-chip" className="flex items-center gap-1.5 mb-1.5 text-[11px] text-[color:var(--ink-3)]">
              <Reply className="w-3 h-3" />Replying to <b className="text-[color:var(--ink-2)]">{replyTo.user_name}</b>
              <button onClick={() => setReplyTo(null)} className="ml-auto hover:text-[color:var(--danger)]"><X className="w-3 h-3" /></button>
            </div>
          )}
          <Textarea data-testid="comment-input" rows={2} value={text} onChange={(e) => setText(e.target.value)}
            placeholder={replyTo ? "Write a reply…" : "Add a comment… use @email to mention"} className="bg-[color:var(--surface)] text-sm" />
          <Button data-testid="comment-post-btn" onClick={post} disabled={busy || !text.trim()} size="sm" className="mt-2 w-full bg-[color:var(--brand-ocean)] hover:bg-[#1E3AD9] text-white">
            {busy ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <SendHorizonal className="w-4 h-4 mr-1" />}{replyTo ? "Post reply" : "Post comment"}
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
}
